import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { connect } from 'react-redux'

import { TokenPayloadInterface } from '@store/actionSlices/token'
import {
  Channel,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import ErrorBoundaryFallback from '@components/error-boundary-fallback'
import HiddenVideo from '@components/hidden-video'

import { hasToken } from '@utilities/helper'
import LightChannelResetter from '@utilities/lightmap-handler/light-channel-resetter'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { setBackground } from '@adUtilities/asset-handler/asset-util'

export interface ContainerProps {
  children?: React.ReactNode
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  token: TokenPayloadInterface
  channels: Array<Channel>
  style?: React.CSSProperties
  background?: {
    url: string
    type: 'legacy' | 'new'
    noSpliceUrl: boolean
    staticUrl?: boolean
  }
  gradiant?: number
  className?: string
  disableNavigation?: boolean
  fullScreenMode?: boolean
}

function Container({
  style,
  children,
  projectIdentity,
  session,
  token,
  channels,
  background,
  gradiant = 0.5,
  className = '',
  disableNavigation = false,
  fullScreenMode,
}: ContainerProps) {
  LightChannelResetter({ projectIdentity, channels })

  const remoteAppIsConnected = React.useMemo(
    () => hasToken(token) && session?.connected,
    [token, session]
  )

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <div
          className={`background-cover relative bg-secondaryColour transition-all duration-300 ${
            !disableNavigation && !(fullScreenMode || remoteAppIsConnected)
              ? 'top-9 h-page-container rounded-t-3xl ipad-mini:top-16'
              : 'top-0 h-screen'
          } ${className}`}
          style={{
            ...setBackground(background ? AssetHandler(background) : '', {
              gradiant,
            }),
            ...style,
          }}
        >
          {children}
        </div>
      </ErrorBoundary>
      {remoteAppIsConnected && (
        <div className="invisible">
          <HiddenVideo />
        </div>
      )}
    </>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session, lightMap },
    token,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
    token,
    channels: lightMap?.channels || [],
  })
)(Container)
